/**
 * 登録フロー用のルートガード
 * 指定された順序でのみ各画面にアクセス可能にする
 */
import React, { useEffect } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useRegistrationFlow } from './RegistrationFlowContext';
import { useAuth } from './AuthContext';

// 登録フロー内のルート一覧を定義
const REGISTRATION_FLOW_ROUTES = [
  '/terms',
  '/auth',
  '/register',
  '/credit-card-input',
  '/registration-complete'
];

// フロー外からもアクセス可能なルート
const ACCESSIBLE_OUTSIDE_FLOW = [
  '/credit-card-input'  // ホームからのクレジットカード登録を許可
];

/**
 * 登録フロールートガードコンポーネント
 */
export const RegistrationRouteGuard = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessRoute, getCurrentStepRoute, getDebugInfo, isInRegistrationFlow } = useRegistrationFlow();
  const { currentUser, isAuthenticated } = useAuth();

  useEffect(() => {
    const currentRoute = location.pathname;

    // 現在のルートが登録フロー内のルートかチェック
    const isRegistrationRoute = REGISTRATION_FLOW_ROUTES.includes(currentRoute);
    
    // 登録フロー内のルートでない場合は何もしない
    if (!isRegistrationRoute) {
      return;
    }

    // 【重要】登録完了画面は特別扱い
    // 登録完了画面への遷移は、フロー完了直後に発生するため、
    // isInRegistrationFlow()がfalseになっていてもアクセスを許可する
    if (currentRoute === '/registration-complete') {
      return;
    }

    // 認証済みユーザーがホームページなどから直接アクセスする場合を許可
    if (ACCESSIBLE_OUTSIDE_FLOW.includes(currentRoute)) {
      // 認証済みユーザーの場合はアクセスを許可
      if (isAuthenticated()) {
        return;
      }
      
      // 未認証かつフロー外の場合は、フロー開始にリダイレクト
      if (!isInRegistrationFlow()) {
        console.warn(`[REGISTRATION GUARD] Unauthenticated access to ${currentRoute} outside flow`);;
        navigate('/terms', { replace: true });
        return;
      }
    }

    // 【修正ポイント】
    // 登録フロー内のルートに直接アクセスした場合、フロー状態をチェック
    // フロー中でない場合は、最初のステップにリダイレクト
    if (!isInRegistrationFlow()) {
      console.warn(`[REGISTRATION GUARD] Direct access to ${currentRoute} without being in flow`);
      navigate('/terms', { replace: true });
      return;
    }
    
    // フロー中の場合、現在のルートにアクセス可能かチェック
    if (!canAccessRoute(currentRoute)) {
      console.warn(`[REGISTRATION GUARD] Unauthorized access to ${currentRoute}`);
      
      // 適切なステップにリダイレクト
      const correctRoute = getCurrentStepRoute();

      // 遅延を設けてAuthContextの状態変更と競合しないようにする
      setTimeout(() => {
        navigate(correctRoute, { replace: true });
      }, 100);
    }
  }, [location.pathname, canAccessRoute, getCurrentStepRoute, navigate, getDebugInfo, isInRegistrationFlow, currentUser, isAuthenticated]);

  return children;
};

/**
 * 認証状態を考慮した登録フロー用のProtectedRoute
 */
export const RegistrationFlowProtectedRoute = ({ children, redirectTo = '/login' }) => {
  const { currentUser, loading } = useAuth();
  const { isInRegistrationFlow } = useRegistrationFlow();
  const location = useLocation();

  // Hooksは常に最上位で呼び出す
  const [waitTime, setWaitTime] = React.useState(0);
  const [hasChecked, setHasChecked] = React.useState(false);

  // 登録フロー中の特定ルートかどうかを判定
  const isRegistrationCompleteRoute = location.pathname === '/registration-complete';
  const isCreditCardInputRoute = location.pathname === '/credit-card-input';
  const isInRegistrationRoutes = isInRegistrationFlow && (isRegistrationCompleteRoute || isCreditCardInputRoute);

  // useEffectも常に呼び出す（条件は内部で判定）
  React.useEffect(() => {
    // クレジットカード入力画面で認証済みの場合は即座に通す
    if (isCreditCardInputRoute && currentUser) {
      setHasChecked(true);
      return;
    }

    // 登録フロー中の特定ルートでない場合は何もしない
    if (!isInRegistrationRoutes) {
      return;
    }

    // 認証完了している場合
    if (currentUser) {
      setHasChecked(true);
      return;
    }
    
    // タイムアウト処理
    if (waitTime < 3000) {
      const timer = setTimeout(() => {
        setWaitTime(prev => prev + 200);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setHasChecked(true);
    }
  }, [currentUser, waitTime, isInRegistrationRoutes, isCreditCardInputRoute]);

  // 認証状態のロード中は専用画面を表示
  if (loading) {
    return (
      <div style={loadingStyles.container}>
        <div style={loadingStyles.spinner}></div>
        <p style={loadingStyles.text}>認証状態を確認中...</p>
      </div>
    );
  }

  // クレジットカード入力画面への認証済みアクセス(フロー外)
  if (isCreditCardInputRoute && !isInRegistrationFlow && (currentUser)) {
    return children;
  }

  // 登録フロー中の特定ルートの場合
  if (isInRegistrationRoutes) {
    
    // 認証完了または3秒経過したら通す
    if (hasChecked || (currentUser)) {
      return children;
    }
    return (
      <div style={loadingStyles.container}>
        <div style={loadingStyles.spinner}></div>
        <p style={loadingStyles.text}>アカウント設定中...</p>
      </div>
    );
  }

  // 登録フロー中でない通常のProtectedRoute処理
  if (!currentUser) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
};

/**
 * 登録フロー管理下のルートに適用するガード
 * 使用例: <RegistrationFlowRoute><RegisterForm /></RegistrationFlowRoute>
 */
export const RegistrationFlowRoute = ({ children }) => {
  return (
    <RegistrationRouteGuard>
      {children}
    </RegistrationRouteGuard>
  );
};

/**
 * 登録フロー管理下のルートに適用するガード
 * 認証必要なステップ用（クレジットカード入力・登録完了画面）
 */
export const RegistrationFlowProtectedGuard = ({ children }) => {
  return (
    <RegistrationRouteGuard>
      <RegistrationFlowProtectedRoute>
        {children}
      </RegistrationFlowProtectedRoute>
    </RegistrationRouteGuard>
  );
};

/**
 * ローディング画面のスタイル
 */
const loadingStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#F0F8FF'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  text: {
    color: '#333',
    fontSize: '16px',
    fontWeight: '500'
  }
};

// CSS アニメーション（インラインで追加）
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// スタイルタグをヘッドに追加（一度だけ）
if (typeof document !== 'undefined' && !document.querySelector('#registration-guard-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'registration-guard-styles';
  styleSheet.textContent = spinKeyframes;
  document.head.appendChild(styleSheet);
}